import axios from 'axios';
import { API_BASE_URL } from '../../../helpers/urlHelper';

// ========================================================================
// 🧪 GESTIÓN DE PLANTILLAS DE PRUEBAS DE LABORATORIO
// ========================================================================

const API_BASE_LAB_TEMPLATES = `${API_BASE_URL}/lab-test-templates`;

// ========================================================================
// 📝 HELPERS PARA CONSTRUCCIÓN DE URLs
// ========================================================================

/**
 * Construye URL para obtener plantilla por ID
 * @param {string} id - ID de la plantilla
 * @returns {string} URL completa
 */
const getLabTemplateDetailsUrl = (id) => `${API_BASE_LAB_TEMPLATES}/${id}`;

/**
 * Construye URL para obtener plantilla por código
 * @param {string} code - Código de la plantilla
 * @returns {string} URL completa
 */
const getLabTemplateByCodeUrl = (code) => `${API_BASE_LAB_TEMPLATES}/code/${code}`;

/**
 * Construye URL para actualizar plantilla
 * @param {string} id - ID de la plantilla
 * @returns {string} URL completa
 */
const getLabTemplateUpdateUrl = (id) => `${API_BASE_LAB_TEMPLATES}/${id}`;

/**
 * Construye URL para eliminar plantilla (soft delete)
 * @param {string} id - ID de la plantilla
 * @returns {string} URL completa
 */
const getLabTemplateDeleteUrl = (id) => `${API_BASE_LAB_TEMPLATES}/${id}`;

/**
 * Construye URL para eliminar plantilla permanentemente
 * @param {string} id - ID de la plantilla
 * @returns {string} URL completa
 */
const getLabTemplateHardDeleteUrl = (id) => `${API_BASE_LAB_TEMPLATES}/${id}/hard`;

/**
 * Construye URL para reactivar plantilla
 * @param {string} id - ID de la plantilla
 * @returns {string} URL completa
 */
const getLabTemplateReactivateUrl = (id) => `${API_BASE_LAB_TEMPLATES}/${id}/reactivate`;

/**
 * Construye URL con query params para listar plantillas
 * @param {Object} params - Parámetros de búsqueda
 * @param {string} params.category - Filtrar por categoría
 * @param {string} params.search - Búsqueda por texto
 * @param {number} params.page - Número de página
 * @param {number} params.limit - Resultados por página
 * @param {string} params.sampleType - Filtrar por tipo de muestra
 * @param {boolean} params.isActive - Filtrar por estado activo
 * @param {boolean} params.requiresFasting - Filtrar por requisito de ayuno
 * @param {boolean} params.isPediatric - Filtrar por uso pediátrico
 * @param {string} params.priority - Filtrar por prioridad (routine, urgent, stat)
 * @returns {string} URL con query params
 */
const getLabTemplatesListUrl = (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.category) queryParams.append('category', params.category);
  if (params.search) queryParams.append('search', params.search);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.sampleType) queryParams.append('sampleType', params.sampleType);
  
  // ✅ CONVERSIÓN EXPLÍCITA A STRING
  if (params.isActive !== undefined) {
    queryParams.append('isActive', String(params.isActive));
  }
  if (params.requiresFasting !== undefined) {
    queryParams.append('requiresFasting', String(params.requiresFasting));
  }
  if (params.isPediatric !== undefined) {
    queryParams.append('isPediatric', String(params.isPediatric));
  }
  
  if (params.priority) queryParams.append('priority', params.priority);
  
  const queryString = queryParams.toString();
  return queryString ? `${API_BASE_LAB_TEMPLATES}?${queryString}` : API_BASE_LAB_TEMPLATES;
};

// ========================================================================
// 📋 SERVICIOS DE CONSULTA
// ========================================================================

/**
 * Obtener todas las plantillas con filtros y paginación
 * @param {Object} params - Parámetros de búsqueda
 * @returns {Promise} Respuesta con plantillas y paginación
 */
export const getAllLabTemplates = async (params = {}) => {
  try {
    const url = getLabTemplatesListUrl(params);
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener plantillas:', error);
    throw error.response?.data || error;
  }
};

/**
 * Obtener plantilla por ID
 * @param {string} id - ID de la plantilla
 * @returns {Promise} Datos de la plantilla
 */
export const getLabTemplateById = async (id) => {
  try {
    const url = getLabTemplateDetailsUrl(id);
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener plantilla por ID:', error);
    throw error.response?.data || error;
  }
};

/**
 * Obtener plantilla por código
 * @param {string} code - Código de la plantilla
 * @returns {Promise} Datos de la plantilla
 */
export const getLabTemplateByCode = async (code) => {
  try {
    const url = getLabTemplateByCodeUrl(code);
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener plantilla por código:', error);
    throw error.response?.data || error;
  }
};

/**
 * Obtener todas las categorías disponibles
 * @returns {Promise} Lista de categorías
 */
export const getLabTemplateCategories = async () => {
  try {
    const response = await axios.get(`${API_BASE_LAB_TEMPLATES}/categories`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener categorías:', error);
    throw error.response?.data || error;
  }
};

/**
 * Obtener todos los tipos de muestra disponibles
 * @returns {Promise} Lista de tipos de muestra
 */
export const getLabTemplateSampleTypes = async () => {
  try {
    const response = await axios.get(`${API_BASE_LAB_TEMPLATES}/sample-types`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener tipos de muestra:', error);
    throw error.response?.data || error;
  }
};

/**
 * Obtener estadísticas de plantillas
 * @returns {Promise} Estadísticas agregadas
 */
export const getLabTemplateStats = async () => {
  try {
    const response = await axios.get(`${API_BASE_LAB_TEMPLATES}/stats`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener estadísticas:', error);
    throw error.response?.data || error;
  }
};

// ========================================================================
// ➕ SERVICIOS DE CREACIÓN
// ========================================================================

/**
 * Crear una nueva plantilla
 * @param {Object} templateData - Datos de la plantilla
 * @returns {Promise} Plantilla creada
 */
export const createLabTemplate = async (templateData) => {
  try {
    const response = await axios.post(API_BASE_LAB_TEMPLATES, templateData);
    return response.data;
  } catch (error) {
    console.error('Error al crear plantilla:', error);
    throw error.response?.data || error;
  }
};

/**
 * Crear múltiples plantillas (importación masiva)
 * @param {Array} templatesArray - Array de plantillas
 * @returns {Promise} Plantillas creadas
 */
export const createMultipleLabTemplates = async (templatesArray) => {
  try {
    const response = await axios.post(`${API_BASE_LAB_TEMPLATES}/bulk`, templatesArray);
    return response.data;
  } catch (error) {
    console.error('Error al crear plantillas múltiples:', error);
    throw error.response?.data || error;
  }
};

// ========================================================================
// ✏️ SERVICIOS DE ACTUALIZACIÓN
// ========================================================================

/**
 * Actualizar plantilla existente
 * @param {string} id - ID de la plantilla
 * @param {Object} templateData - Datos actualizados
 * @returns {Promise} Plantilla actualizada
 */
export const updateLabTemplate = async (id, templateData) => {
  try {
    const url = getLabTemplateUpdateUrl(id);
    const response = await axios.put(url, templateData);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar plantilla:', error);
    throw error.response?.data || error;
  }
};

/**
 * Actualizar parcialmente una plantilla
 * @param {string} id - ID de la plantilla
 * @param {Object} partialData - Datos parciales a actualizar
 * @returns {Promise} Plantilla actualizada
 */
export const patchLabTemplate = async (id, partialData) => {
  try {
    const url = getLabTemplateUpdateUrl(id);
    const response = await axios.patch(url, partialData);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar parcialmente plantilla:', error);
    throw error.response?.data || error;
  }
};

// ========================================================================
// 🗑️ SERVICIOS DE ELIMINACIÓN Y GESTIÓN DE ESTADO
// ========================================================================

/**
 * Eliminar plantilla (soft delete - desactivar)
 * @param {string} id - ID de la plantilla
 * @returns {Promise} Confirmación de eliminación
 */
export const deleteLabTemplate = async (id) => {
  try {
    const url = getLabTemplateDeleteUrl(id);
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar plantilla:', error);
    throw error.response?.data || error;
  }
};

/**
 * Eliminar plantilla permanentemente (hard delete)
 * @param {string} id - ID de la plantilla
 * @returns {Promise} Confirmación de eliminación permanente
 */
export const hardDeleteLabTemplate = async (id) => {
  try {
    const url = getLabTemplateHardDeleteUrl(id);
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar permanentemente plantilla:', error);
    throw error.response?.data || error;
  }
};

/**
 * Reactivar plantilla desactivada
 * @param {string} id - ID de la plantilla
 * @returns {Promise} Plantilla reactivada
 */
export const reactivateLabTemplate = async (id) => {
  try {
    const url = getLabTemplateReactivateUrl(id);
    const response = await axios.patch(url);
    return response.data;
  } catch (error) {
    console.error('Error al reactivar plantilla:', error);
    throw error.response?.data || error;
  }
};

// ========================================================================
// 🔧 SERVICIOS AUXILIARES
// ========================================================================

/**
 * Validar código de plantilla único
 * @param {string} code - Código a validar
 * @param {string} excludeId - ID a excluir de la validación (para updates)
 * @returns {Promise<boolean>} true si el código está disponible
 */
export const validateLabTemplateCode = async (code, excludeId = null) => {
  try {
    const response = await getLabTemplateByCode(code);
    
    // Si encuentra una plantilla con ese código
    if (response.success && response.data) {
      // Si estamos actualizando y es la misma plantilla, el código es válido
      if (excludeId && response.data._id === excludeId) {
        return true;
      }
      // Si es otra plantilla, el código no está disponible
      return false;
    }
    
    return true;
  } catch (error) {
    // Si el error es 404, el código está disponible
    if (error.status === 404) {
      return true;
    }
    throw error;
  }
};

/**
 * Buscar plantillas por texto (búsqueda simplificada)
 * @param {string} searchText - Texto a buscar
 * @param {number} limit - Límite de resultados
 * @returns {Promise} Lista de plantillas encontradas
 */
export const searchLabTemplates = async (searchText, limit = 20) => {
  return getAllLabTemplates({
    search: searchText,
    limit: limit,
    isActive: true
  });
};

/**
 * Obtener plantillas activas por categoría
 * @param {string} category - Categoría a filtrar
 * @returns {Promise} Lista de plantillas de la categoría
 */
export const getLabTemplatesByCategory = async (category) => {
  return getAllLabTemplates({
    category: category,
    isActive: true
  });
};

/**
 * Obtener plantillas por tipo de muestra
 * @param {string} sampleType - Tipo de muestra
 * @returns {Promise} Lista de plantillas del tipo de muestra
 */
export const getLabTemplatesBySampleType = async (sampleType) => {
  return getAllLabTemplates({
    sampleType: sampleType,
    isActive: true
  });
};

/**
 * Obtener plantillas pediátricas
 * @returns {Promise} Lista de plantillas pediátricas
 */
export const getPediatricLabTemplates = async () => {
  return getAllLabTemplates({
    isPediatric: true,
    isActive: true
  });
};

/**
 * Obtener plantillas que requieren ayuno
 * @returns {Promise} Lista de plantillas que requieren ayuno
 */
export const getFastingLabTemplates = async () => {
  return getAllLabTemplates({
    requiresFasting: true,
    isActive: true
  });
};

/**
 * Obtener plantillas por prioridad
 * @param {string} priority - Prioridad (routine, urgent, stat)
 * @returns {Promise} Lista de plantillas con esa prioridad
 */
export const getLabTemplatesByPriority = async (priority) => {
  return getAllLabTemplates({
    priority: priority,
    isActive: true
  });
};

// ========================================================================
// 📤 EXPORTAR HELPERS DE URLs (opcional, si se necesitan en otros módulos)
// ========================================================================

export const labTemplateUrlHelpers = {
  getLabTemplateDetailsUrl,
  getLabTemplateByCodeUrl,
  getLabTemplateUpdateUrl,
  getLabTemplateDeleteUrl,
  getLabTemplateHardDeleteUrl,
  getLabTemplateReactivateUrl,
  getLabTemplatesListUrl
};

// ========================================================================
// 📦 EXPORTACIÓN POR DEFECTO
// ========================================================================

export default {
  // Consulta
  getAllLabTemplates,
  getLabTemplateById,
  getLabTemplateByCode,
  getLabTemplateCategories,
  getLabTemplateSampleTypes,
  getLabTemplateStats,
  
  // Creación
  createLabTemplate,
  createMultipleLabTemplates,
  
  // Actualización
  updateLabTemplate,
  patchLabTemplate,
  
  // Eliminación
  deleteLabTemplate,
  hardDeleteLabTemplate,
  reactivateLabTemplate,
  
  // Auxiliares
  validateLabTemplateCode,
  searchLabTemplates,
  getLabTemplatesByCategory,
  getLabTemplatesBySampleType,
  getPediatricLabTemplates,
  getFastingLabTemplates,
  getLabTemplatesByPriority,
  
  // Helpers de URLs
  urlHelpers: labTemplateUrlHelpers
};